import React from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import AlertSeverityBadge from "./AlertSeverityBadge";
import AlertMetricLines from "./AlertMetricLines";
import {
  formatAbsoluteDate,
  formatFrequencyBadge,
  formatRelativeTime,
  maxZScore,
} from "util/cxAlertModel";
import cxStyles from "./cxAlert.module.scss";

export default function AlertPanelItem({ record, variant = "default", theme, isDark }) {
  const { t } = useTranslation();
  const payload = record?.last_payload;
  const firedAt = payload?.fired_at ?? record?.last_fired_at;
  const zScore = maxZScore(payload);
  const isPopup = variant === "popup";
  const isDarkPopup = isPopup && (theme === "dark" || isDark);

  const itemClass = [
    isPopup ? cxStyles.panelItemPopup : cxStyles.panelItem,
    isDarkPopup ? cxStyles.panelItemPopupDark : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!payload && !firedAt) {
    if (isPopup) {
      return (
        <div className={itemClass}>
          <div className={cxStyles.panelTitlePopup}>{record.kpi_name}</div>
          <div className={cxStyles.panelMetaPopup}>
            {t("alerts_page.configured_no_fire")}
          </div>
        </div>
      );
    }

    return (
      <div className={itemClass}>
        <div className={cxStyles.panelHeader}>
          <div className={cxStyles.panelTitle}>{record.kpi_name}</div>
        </div>
        <p className={cxStyles.metricLine}>{t("alerts_page.configured_no_fire")}</p>
      </div>
    );
  }

  if (isPopup) {
    return (
      <div className={itemClass}>
        <div className={cxStyles.panelHeaderPopup}>
          <div className={cxStyles.panelTitlePopup}>
            {payload?.severity && (
              <>
                <AlertSeverityBadge severity={payload.severity} />{" "}
              </>
            )}
            {record.kpi_name}
          </div>
          <Tooltip title={formatAbsoluteDate(firedAt)}>
            <span className={cxStyles.panelTimePopup}>
              {formatRelativeTime(firedAt, t)}
            </span>
          </Tooltip>
        </div>
        <AlertMetricLines payload={payload} popup />
        <div className={cxStyles.panelFooterPopup}>
          {zScore != null && (
            <span>
              {t("alerts_page.z_score_label")}: {Number(zScore).toFixed(1)} ·{" "}
            </span>
          )}
          {formatFrequencyBadge(payload?.frequency ?? record.primary_frequency, t)}{" "}
          {t("alerts_page.alarm_suffix")}
        </div>
        {payload?._mock && (
          <div className={cxStyles.mockNote}>{t("alerts_page.mock_payload_note")}</div>
        )}
      </div>
    );
  }

  return (
    <div className={itemClass}>
      <div className={cxStyles.panelHeader}>
        <div className={cxStyles.panelTitle}>
          {payload?.severity && (
            <AlertSeverityBadge severity={payload.severity} />
          )}{" "}
          {record.kpi_name}
        </div>
        <Tooltip title={formatAbsoluteDate(firedAt)}>
          <span className={cxStyles.panelTime}>
            {formatRelativeTime(firedAt, t)}
          </span>
        </Tooltip>
      </div>
      <AlertMetricLines payload={payload} />
      <div className={cxStyles.panelFooter}>
        {zScore != null && (
          <span>
            {t("alerts_page.z_score_label")}: {Number(zScore).toFixed(1)} ·{" "}
          </span>
        )}
        {formatFrequencyBadge(payload?.frequency ?? record.primary_frequency, t)}{" "}
        {t("alerts_page.alarm_suffix")}
      </div>
      {payload?._mock && (
        <div className={cxStyles.mockNote}>{t("alerts_page.mock_payload_note")}</div>
      )}
    </div>
  );
}
